import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import ProblemIndicator from "./ProblemIndicator";

export function formatDate(date) {
  if (!date) return "";

  const d = moment(date);
  if (!d.isValid()) return "";
  return d.format("YYYY-MM-DD");
}

export default function Input({
  type,
  label,
  suffix,
  name,
  value,
  onChange,
  description,
  required,
  error,
  formErrors,
  ...rest
}) {
  // This allows us to have a controlled date input that still functions more like a native one.
  const [controlledValue, setControlledValue] = useState(() => {
    if (type !== "date") return;
    return formatDate(value);
  });

  const [hasDateError, setHasDateError] = useState(false);
  const hasAddedNumberHack = useRef(false);

  useEffect(() => {
    if (type === 'date') {
      const formatted = formatDate(value);
      if (formatted !== controlledValue) {
        setControlledValue(formatted);
      }
      if (hasDateError && formatted !== '') {
        setHasDateError(false);
      }
    } else if (controlledValue != undefined) {
      setControlledValue(undefined);
      setHasDateError(false);
    }
  }, [type, type === 'date' ? value : undefined]);


  if (error === undefined && name && formErrors) {
    error = formErrors.find((p) => p.field === name);

    if (!error && hasDateError) {
      error = { message: "Invalid Date", level: "error" };
    }
  }

  return (
    <div className="flex flex-col flex-1">
      {label && <label htmlFor="name" className="text-nowrap">
        {label}
        {required && <span className="text-red-600">&nbsp;*</span>}
      </label>}
      <div
        className={`
          flex items-center border rounded bg-white pr-1
          focus-within:border-blue-500 [&:has(input:is(:disabled))]:bg-stone-300
          ${hasDateError ? "border-red-500" : "border-stone-400"}
        `}
      >
        {type === 'textarea'
          ? <textarea
            className="primary-input rounded flex-1 border-none -mr-1 focus:ring-0 disabled:bg-stone-300"
            name={name}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={rest.disabled}
          />
          : <div className="flex-1">
            <input
              className={`
                primary-input rounded border-none focus:ring-0 disabled:bg-stone-300
                disabled:text-stone-600 h-9 w-full max-w-full
              `}
              name={name}
              type={type}
              value={type === "date" ? controlledValue : value}
              onChange={(e) => {
                if (type !== "date") {
                  if (type === "number") {
                    // Pass the value as a number for convenience, but also as a string
                    // in case we need it for processing the value on a specific input.
                    onChange?.(
                      isNaN(e.target.valueAsNumber) ? '' : e.target.valueAsNumber,
                      e.target.value,
                    );
                  } else {
                    onChange?.(e.target.value);
                  }
                  return;
                }

                setControlledValue(e.target.value);

                if (!e.target.valueAsDate) return;

                if (hasDateError) {
                  setHasDateError(false);
                }

                const date = new Date(e.target.valueAsDate);
                // Timezones are weird on date inputs.
                // TODO: Did some rough testing on this, but it needs to be more thoroughly vetted.
                date.setMinutes(date.getMinutes() + date.getTimezoneOffset());

                if (formatDate(date) !== e.target.value) return;
                onChange?.(date);
              }}
              onBlur={type === "date" ? (e) => {
                if (!e.target.validity.valid) {
                  onChange?.('');
                  setHasDateError(true);
                }
              } : undefined}
              onFocus={(event) => {
                if (type !== "number" || hasAddedNumberHack.current) return;
                // HACK: Fixes an issue where number inputs in a scrollable area get weird.
                hasAddedNumberHack.current = true;
                event.target.addEventListener("wheel", (e) => {
                  e.preventDefault();
                }, { passive: false });
              }}
              {...rest}
            />
          </div>
        }
        {suffix && <div className="text-stone-600">{suffix}</div>}
      </div>
      {error && <ProblemIndicator level={error.displayLevel ?? error.level} message={error.message} />}
      {description && <span className="text-sm text-gray-500">{description}</span>}
    </div>
  );
}
